/**
 * Admin API client - uses same base URL, sends Bearer token for protected routes.
 * 401 interceptor: tries refresh token, retries request; on failure clears auth and redirects.
 * Backend response: { status, message, message_code, data }
 */
import { API_BASE_URL } from "./client";
import {
  getAdminToken,
  setAdminToken,
  getAdminRefreshToken,
  setAdminRefreshToken,
  clearAdminAuth,
  updateAdminLastActivity,
} from "../utils/adminAuth";

function getHeaders(includeAuth = true, contentType = "application/json", token = null) {
  const headers = {};
  if (contentType) headers["Content-Type"] = contentType;
  if (includeAuth) {
    const t = token ?? getAdminToken();
    if (t) {
      headers.Authorization = `Bearer ${t}`;
      updateAdminLastActivity();
    }
  }
  return headers;
}

async function adminRefreshToken() {
  const refresh = getAdminRefreshToken();
  if (!refresh) return null;
  const res = await fetch(`${API_BASE_URL}/admin/auth/refresh`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refresh_token: refresh }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) return null;
  const data = json?.data ?? json;
  const token = data?.token ?? data?.access_token;
  if (token) {
    setAdminToken(token);
    if (data?.refresh_token) setAdminRefreshToken(data.refresh_token);
    return token;
  }
  return null;
}

function isAuthEndpoint(path) {
  if (!path) return false;
  return (
    path.includes("/admin/auth/login") ||
    path.includes("/admin/auth/refresh") ||
    path.includes("/admin/auth/register") ||
    path.includes("/admin/auth/forgot-password")
  );
}

async function handleResponse(res, skip401Redirect = false) {
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    let msg = json?.message;
    if (!msg && Array.isArray(json?.detail)) {
      const first = json.detail[0];
      msg = first?.msg || first?.message || (first?.loc ? `${first.loc.join(".")}: ${first.msg || "Invalid"}` : null);
    }
    if (!msg && json?.data?.errors) {
      const errs = Object.values(json.data.errors).flat();
      msg = Array.isArray(errs) ? errs[0] : String(errs);
    }
    const err = new Error(msg || json?.detail || `Request failed with status ${res.status}`);
    err.status = res.status;
    err.message_code = json?.message_code;
    err.response = json;
    if (res.status === 401) err.isSessionExpired = true;
    if (res.status === 401 && !skip401Redirect) {
      clearAdminAuth();
      if (typeof window !== "undefined" && !window.location.pathname.includes("/admin/login")) {
        window.location.href = "/admin/login";
      }
    }
    throw err;
  }
  return json;
}

/** Fetch with 401 retry via refresh token. options: method, body, includeAuth, contentType */
async function adminFetch(path, options = {}, retried = false) {
  const url = `${API_BASE_URL}${path}`;
  const { includeAuth = true, contentType = "application/json", token, ...rest } = options;
  const headers = getHeaders(includeAuth, contentType, token);
  const res = await fetch(url, {
    ...rest,
    headers: { ...headers, ...(rest.headers || {}) },
  });
  const hasRefresh = getAdminRefreshToken();
  if (res.status === 401 && !retried && !isAuthEndpoint(path)) {
    if (hasRefresh) {
      const newToken = await adminRefreshToken();
      if (newToken) {
        return adminFetch(path, { ...options, token: newToken }, true);
      }
    }
    clearAdminAuth();
    if (typeof window !== "undefined" && !window.location.pathname.includes("/admin/login")) {
      window.location.href = "/admin/login";
    }
    const err = new Error("Session expired. Please login again.");
    err.status = 401;
    err.isSessionExpired = true;
    throw err;
  }
  return res;
}

/** POST /admin/upload/media - FormData with file + product_id + media_type. */
export async function adminUploadImage(formData) {
  const res = await adminFetch("/admin/upload/media", {
    method: "POST",
    body: formData,
    contentType: null,
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(json?.message || json?.detail || `Upload failed ${res.status}`);
    err.status = res.status;
    throw err;
  }
  const data = json?.data ?? json;
  if (data?.media_url && !data?.image_url) {
    return { ...data, image_url: data.media_url, url: data.media_url };
  }
  return data;
}

/** POST /admin/auth/logout - revoke current token (or all_devices). Call before clearAdminAuth. */
export async function adminLogout(allDevices = false) {
  try {
    const params = allDevices ? "?all_devices=true" : "";
    await adminFetch(`/admin/auth/logout${params}`, { method: "POST" });
  } catch (_) {
    /* Best-effort: clear local auth even if backend fails */
  } finally {
    clearAdminAuth();
  }
}

/** POST /admin/auth/login - returns full response so caller can read data.token, data.refresh_token, data.user */
export async function adminLogin(email, password) {
  const res = await adminFetch("/admin/auth/login", {
    method: "POST",
    includeAuth: false,
    body: JSON.stringify({ email: email.trim(), password }),
  });
  const json = await handleResponse(res);
  return json?.data ?? json;
}

/** GET admin endpoint - returns response.data */
export async function adminGet(path) {
  const res = await adminFetch(path, { method: "GET" });
  const json = await handleResponse(res);
  return json?.data ?? null;
}

/** POST admin endpoint - returns response.data */
export async function adminPost(path, body) {
  const res = await adminFetch(path, { method: "POST", body: JSON.stringify(body) });
  const json = await handleResponse(res);
  return json?.data ?? null;
}

/** PUT admin endpoint - returns response.data */
export async function adminPut(path, body) {
  const res = await adminFetch(path, { method: "PUT", body: JSON.stringify(body) });
  const json = await handleResponse(res);
  return json?.data ?? null;
}

/** DELETE admin endpoint - returns response.data */
export async function adminDelete(path) {
  const res = await adminFetch(path, { method: "DELETE" });
  const json = await handleResponse(res);
  return json?.data ?? null;
}

/** PATCH admin endpoint - returns response.data */
export async function adminPatch(path, body) {
  const res = await adminFetch(path, { method: "PATCH", body: JSON.stringify(body) });
  const json = await handleResponse(res);
  return json?.data ?? null;
}
